import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { useAuth } from "@/lib/auth-context";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  DEFAULT_PREFERENCES,
  type NotificationPreferences,
} from "@/lib/notification-preferences";
import {
  getMyNotificationPreferences,
  updateMyNotificationPreferences,
} from "@/lib/notifications.functions";

export const Route = createFileRoute("/_authenticated/settings/notifications")({
  head: () => ({
    meta: [
      { title: "Notification settings — Quibblies" },
      {
        name: "description",
        content:
          "Choose which Quibblies updates reach your inbox and which stay in the app, from moderation decisions to community activity.",
      },
      { property: "og:title", content: "Notification settings — Quibblies" },
      {
        property: "og:description",
        content: "Control which Quibblies updates you receive by email and in-app.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: NotificationSettingsPage,
});

const OPTIONS: Array<{
  key: keyof NotificationPreferences;
  label: string;
  description: string;
}> = [
  {
    key: "email_moderation",
    label: "Moderation decisions by email",
    description: "Profile image and publication decisions land in your inbox.",
  },
  {
    key: "inapp_moderation",
    label: "Moderation decisions in-app",
    description: "The same decisions appear in your notifications.",
  },
  {
    key: "email_community",
    label: "Community activity by email",
    description: "New followers, comments and replies on your writing.",
  },
  {
    key: "inapp_community",
    label: "Community activity in-app",
    description: "Follows, comments and replies in your notification list.",
  },
  {
    key: "inapp_tip_success",
    label: "Successful tips in-app",
    description: "A notice when a tip is collected — as the writer or the supporter.",
  },
  {
    key: "email_tip_success",
    label: "Successful tips by email",
    description: "A confirmation email for every tip that goes through. Off by default.",
  },
  {
    key: "inapp_tip_refund",
    label: "Refunded tips in-app",
    description: "When a tip is refunded or fails to collect.",
  },
  {
    key: "email_tip_refund",
    label: "Refunded tips by email",
    description: "The same refund and failure updates in your inbox.",
  },
  {
    key: "inapp_tip_dispute",
    label: "Disputed tips in-app",
    description: "When a supporter's bank charges a tip back.",
  },
  {
    key: "email_tip_dispute",
    label: "Disputed tips by email",
    description: "Chargebacks emailed to you — these usually need a response.",
  },
  {
    key: "inapp_payout",
    label: "Payout updates in-app",
    description:
      "Each step of a payout request — received, approved, processing, sent or failed.",
  },
  {
    key: "email_payout",
    label: "Payout updates by email",
    description: "The same payout progress updates, including any failure reason, by email.",
  },
  {
    key: "email_product",
    label: "Product updates by email",
    description: "Occasional notes about new Quibblies features. Off by default.",
  },
];

const STAFF_OPTIONS: Array<{
  key: keyof NotificationPreferences;
  label: string;
  description: string;
}> = [
  {
    key: "inapp_staff_message",
    label: "Team messages in-app",
    description: "Private messages and replies from teammates raise your badge count.",
  },
  {
    key: "email_staff_message",
    label: "Team messages by email",
    description: "Get a copy of team messages in your inbox. Off by default.",
  },
  {
    key: "inapp_team_admin",
    label: "Team and queue alerts in-app",
    description: "Role changes, invitations and moderation queue alerts.",
  },
  {
    key: "email_team_admin",
    label: "Team and queue alerts by email",
    description: "The same team and queue alerts sent to your inbox.",
  },
];

function NotificationSettingsPage() {
  const queryClient = useQueryClient();
  const { isStaff } = useAuth();
  const load = useServerFn(getMyNotificationPreferences);
  const save = useServerFn(updateMyNotificationPreferences);
  const [draft, setDraft] = React.useState<NotificationPreferences | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () => load(),
    retry: false,
  });

  React.useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (next: Partial<NotificationPreferences>) => save({ data: next }),
    onSuccess: () => {
      toast.success("Notification preferences saved");
      void queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
    },
    onError: (e: Error) => toast.error("Could not save", { description: e.message }),
  });

  const values = draft ?? DEFAULT_PREFERENCES;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Notifications"
        description="Choose what reaches you. Decisions about your own account — approvals, rejections, appeals and security notices — are always sent."
      />

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : (
        <div className="mt-8 space-y-3">
          {OPTIONS.map((option) => (
            <label
              key={String(option.key)}
              className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5"
            >
              <span>
                <span className="block text-sm font-medium text-foreground">{option.label}</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {option.description}
                </span>
              </span>
              <Switch
                checked={values[option.key]}
                onCheckedChange={(checked) => {
                  const next = { ...values, [option.key]: checked };
                  setDraft(next);
                  mutation.mutate({ [option.key]: checked });
                }}
              />
            </label>
          ))}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-border/60 bg-surface/40 p-5 text-sm text-muted-foreground">
        <p className="text-foreground">Unsubscribing completely</p>
        <p className="mt-2">
          Every email we send carries an unsubscribe link in its footer. Using it stops all
          Quibblies emails to that address straight away — sign-in and password reset messages keep
          working, and your account itself is unaffected. You can resubscribe from the same link.
        </p>
      </div>
    </div>
  );
}
